import moment from 'moment-timezone'

import {
  DATE_FORMAT,
  DATETIME_FORMAT,
  TIME_FORMAT,
  SHORT_DATE_FORMAT,
  daysOfTheWeek
} from '@/lib/constants'

/**
 * Formats a subreddit to include the r/ prefix.
 * @param {string} subredditName
 */
export function formatSubreddit(subredditName) {
  if (subredditName.startsWith('/r/') || subredditName.startsWith('r/')) {
    return subredditName
  } else {
    return `r/${subredditName}`
  }
}

/**
 * Formats a subreddit to include the u/ prefix.
 */
export function formatUsername(username) {
  if (username.startsWith('/u/') || username.startsWith('u/')) {
    return username
  } else {
    return `u/${username}`
  }
}

export function formatDateTime(date) {
  return moment(date).format(DATETIME_FORMAT)
}

export function formatDate(date, format = DATE_FORMAT) {
  return moment(date).format(format)
}

export function formatTime(date) {
  return moment(date).format('h:mm a')
}

export function formatDueDate(dueDate, format = DATE_FORMAT) {
  const isToday = moment().diff(dueDate, 'days') === 0

  if (isToday) {
    return `today at ${moment(dueDate).format(TIME_FORMAT)}`
  }

  return formatDate(dueDate, format)
}

export function formatExpirationDate(date, expiresInDays) {
  date = moment(date)

  const expirationDate = date.add(expiresInDays, 'days')
  const now = moment()

  const delta = moment.duration(expirationDate.diff(now))

  const days = delta.days()
  const hours = delta.hours()
  const minutes = delta.minutes()

  if (days > 0) {
    return `${days} ${pluralize(days, 'day')}`
  } else if (hours > 0) {
    return `${hours} ${pluralize(hours, 'hour')}`
  } else {
    return `${minutes} ${pluralize(minutes, 'minute')}`
  }
}

export function formatHour(hoursInDay, displayMinutes = true) {
  hoursInDay = hoursInDay % 24

  let hour = hoursInDay
  let amPM = 'am'

  if (hoursInDay >= 12) {
    amPM = 'pm'
    if (hoursInDay !== 12) {
      hour = hoursInDay - 12
    }
  } else if (hoursInDay === 0) {
    hour = 12
  }
  if (!displayMinutes) {
    return `${hour} ${amPM}`
  }
  return `${hour}:00 ${amPM}`
}

export function formatNumber(value, digits = 1) {
  const formatter = Intl.NumberFormat('en', {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits
  })
  return formatter.format(value).toLowerCase()
}

export function formatDateRange(dates) {
  return `${formatDate(dates[0], SHORT_DATE_FORMAT)} - ${formatDate(
    dates[1],
    SHORT_DATE_FORMAT
  )}`
}

export function formatDuration(milliseconds) {
  const minutes = Math.floor(milliseconds / 60000)
  const seconds = Math.floor((milliseconds % 60000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function getMinutesBetweenDates(startDate, endDate) {
  const diff = endDate.getTime() - startDate.getTime()
  return Math.floor(diff / 60000)
}

export function dayOfTheWeekFromPython(dayIndex) {
  let day = null
  if (dayIndex === 6) {
    day = 0
  } else {
    day = dayIndex + 1
  }
  return daysOfTheWeek[day]
}

export function ensureMaxCharacters(string, max) {
  if (string.length > max) {
    return string.substring(0, max - 3) + '...'
  } else {
    return string
  }
}

export function parseQSArray(qsArray) {
  if (typeof qsArray === 'string') {
    return qsArray.split(',')
  } else if (Array.isArray(qsArray)) {
    return qsArray
  } else {
    return []
  }
}

export function titleCase(string) {
  return string.toLowerCase().replace(/(?:^|\s)\w/g, function (match) {
    return match.toUpperCase()
  })
}

export function getRandomMinuteInterval() {
  let minutes = Math.floor(Math.random() * 55)
  return (minutes = Math.round(minutes / 2) * 2)
}

export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function isDeviceMobile() {
  return window.matchMedia('only screen and (max-width: 768px)').matches
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getBrowserTimezone() {
  return moment.tz.guess() || 'UTC'
}

export function convertToTimezone(value, fromTz, toTz) {
  // We get the timezone offset from the given date so that seasonal changes like daylight savings are handled.
  const offset =
    moment(value).tz(fromTz).utcOffset() - moment(value).tz(toTz).utcOffset()
  const adjustedValue = new Date(value.getTime() - offset * 60000)
  return adjustedValue
}

export function ensureHttp(link) {
  if (link && typeof link === 'string' && !link.startsWith('http')) {
    if (link.startsWith('//')) {
      return 'https:' + link
    } else {
      return 'https://' + link
    }
  } else {
    return link
  }
}

export function extractDomain(urlString) {
  try {
    let url = new URL(urlString)
    return url.hostname
  } catch (e) {
    return urlString
  }
}

export function isValidUrl(url) {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

export function hasSpintax(text) {
  // We created our own `hasSpintax` function because we don't want to
  // consider brackets with a single option as Spintax.
  // This is the expected behavior:
  //   hasSpintax('{a}') => false
  //   hasSpintax('{a|b}') => true
  //   hasSpintax('{a|}') => true
  const spintaxRegex = /{[^{}]*\|[^{}\\]*}/
  return spintaxRegex.test(text)
}

export function pluralize(amount, word) {
  return amount > 1 || amount == 0 ? `${word}s` : word
}

/**
 * Convert a boolean string or number to boolean.
 * 'false', 0 -> false
 * 'true', 1 -> true
 */
export function toBoolean(value) {
  if (typeof value === 'boolean') {
    return value
  }
  if (!value) {
    return false
  }
  return ['1', 'true', 'yes', 'y'].includes(value.toLowerCase())
}

export function moveUpSubmissionTree(target, submissions) {
  const submissionTree = []
  const buildSubmissionTree = (submission) => {
    if (submission) {
      submissionTree.push(submission)
      submissions
        .filter((sub) => sub.crosspostFrom?.$id === submission.$id)
        .forEach((sub) => buildSubmissionTree(sub))
    }
  }
  const findPreviousSibling = (submission) => {
    const siblings = submissions.filter(
      (sub) => sub.crosspostFrom?.$id === submission.crosspostFrom?.$id
    )
    const index = siblings.findIndex((sub) => sub.$id === submission.$id)
    return siblings[index - 1]
  }

  // Find the submission and its descendants.
  buildSubmissionTree(target)

  const previousSibling = findPreviousSibling(target)

  if (previousSibling) {
    const submissionIndex = submissions.indexOf(target)
    // Remove the submission tree from its current position.
    submissions.splice(submissionIndex, submissionTree.length)
    // Insert the submission tree before its previous sibling.
    const moveToIndex = submissions.indexOf(previousSibling)
    submissions.splice(moveToIndex, 0, ...submissionTree)
  }

  return submissions
}

export function moveDownSubmissionTree(target, submissions) {
  const findNextSibling = (submission) => {
    const siblings = submissions.filter(
      (sub) => sub.crosspostFrom?.$id === submission.crosspostFrom?.$id
    )
    const index = siblings.findIndex((sub) => sub.$id === submission.$id)
    return siblings[index + 1]
  }

  const nextSibling = findNextSibling(target)
  if (!nextSibling) {
    return submissions
  }
  // Instead of moving down the target, we move its next sibling up.
  return moveUpSubmissionTree(nextSibling, submissions)
}

/**
 * Converts an HTML input like: '<p>That&#39;s <strong>right</strong>!</p>'
 * To a plain text output like: 'That's right!'
 */
export function cleanHTML(html) {
  if (!html) {
    return ''
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  let textContent = doc.body.textContent || ''
  // Remove new lines and tabs.
  textContent = textContent.replace(/[\n\t]/g, '')

  return textContent
}

export function cleanSubredditName(input) {
  if (!input) {
    return ''
  }

  let subreddit = input

  // Clean the subreddit from any errant /r/ prefixes or slashes
  subreddit = subreddit.replace('https://www.reddit.com/r/', '')
  subreddit = subreddit.replace(/\s+/g, '')
  subreddit = subreddit.replace('/r/', '')
  subreddit = subreddit.replace('r/', '')
  subreddit = subreddit.replace(/\//g, '')

  return subreddit.toLowerCase()
}

export function formatBoolean(value) {
  if (value === undefined || value === null) {
    return ''
  }
  return value ? 'Yes' : 'No'
}
